import { useTranslation } from "react-i18next";
import { Button as AriaButton } from "react-aria-components";
import { ArchiveRestore, ChevronRight } from "lucide-react";
import type { FacilityTreeNode } from "../facility-tree.js";
import { FacilityCountsMeta } from "../FacilityCountsMeta.js";
import { FacilityKebabMenu } from "../FacilityKebabMenu.js";
import { FavoriteStarButton } from "./FavoriteStarButton.js";
import type { KebabProps } from "./useDrilldownActions.js";
import { Button, NavButton } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { facilityDrillHref, facilityManualsHref } from "./nav-state.js";

/**
 * One facility as a card in the drill-down list (FN / ADR-014; redesigned 2026-10-21 — the "Bereichsauswahl Redesign"
 * canvas). Layout, left to right: name + favourite star over the spelled-out counts, then the labelled actions:
 * - **Manuals** (brand-orange fill, white ink — the owner-approved `--on-orange` exception) — shown iff direct
 *   `permission !== null` AND the facility has content (plan-panel #1: a count-only rule would let a descendant-only
 *   user open an ancestor's manuals).
 * - **Unterbereiche** (outline, chevron) — shown iff the node has children; drills one level deeper. Ancestors are
 *   navigable regardless of permission (that's how you reach a permitted descendant).
 * - **Kebab** (⋮) — LAST; the host passes `kebab = null` outside edit mode or when the caller can't act.
 *
 * Slots: Manuals sits LEFT of Unterbereiche. A node WITH children but no manuals keeps an invisible Manuals placeholder,
 * so Unterbereiche stays in its column across rows; a node with NO children drops the Unterbereiche slot entirely, so
 * Manuals moves to the far right (owner request). Below `sm` the actions drop under the name as two equal columns, or one above the other when a
 * tenant's word is too long for that (Takeda's "Handlungsanweisungen"), and the placeholder goes.
 *
 * **Selection** (`onSelect`, wide screens only — the host mounts the preview panel): a transparent, labelled button
 * covers the card ("Alpha auswählen", `aria-pressed`), so a click on any free card area selects it; the real controls
 * sit above it (`relative`) and keep their own behaviour. Selected = an orange frame (+ the orange timeline dot).
 *
 * **Variants:**
 * - *plain* (default) — the root list: no timeline.
 * - *timeline* — a drill level's children: a dot on the rail the LIST draws, connecting them to the level header.
 * - *flat* (`pathLabel` set) — the ancestor path breadcrumb sits above the card (Favourites / search results).
 * - *restore-only* (`archived`) — no star, no kebab, no nav; an Unarchive button on an archived-subtree ROOT only.
 */
export function FacilityRailCard({
  node,
  index,
  kebab,
  isFavorite,
  onToggleFavorite,
  onDrill,
  onOpenManuals,
  pathLabel,
  navigable = true,
  archived = false,
  showFavorite = true,
  onUnarchive,
  timeline = false,
  selected = false,
  onSelect,
}: {
  node: FacilityTreeNode;
  index: number;
  kebab: KebabProps | null;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
  onDrill: (id: number) => void;
  onOpenManuals: (id: number) => void;
  /** When set, render the FLAT variant: this ancestor-path breadcrumb sits above the card. */
  pathLabel?: string;
  /** Show the Manuals / Unterbereiche nav controls. `false` for cards that can't be navigated (archived). */
  navigable?: boolean;
  /** Restore-only mode (the Archived list) — hides star/kebab/nav; only an Unarchive button (if `onUnarchive`). */
  archived?: boolean;
  /** Show the favourite star. `false` for the shop-floor kiosk (ADR-036): a shared account has no "my" favourites. */
  showFavorite?: boolean;
  /** Unarchive this facility (given only for archived-subtree ROOTS) → renders the Unarchive button. */
  onUnarchive?: () => void;
  /** Draw this card's dot on the list's timeline rail (a drill level's children). */
  timeline?: boolean;
  /** The card is the one shown in the preview panel. */
  selected?: boolean;
  /** Select this card for the preview panel. Absent ⇒ the card is not selectable (no panel on narrow screens). */
  onSelect?: (id: number) => void;
}) {
  const { t } = useTranslation();
  const flat = pathLabel !== undefined;
  const hasChildren = !archived && node.childCount > 0;
  const canOpenManuals = !archived && node.permission !== null && ((node.manualCount ?? 0) > 0 || (node.categoryCount ?? 0) > 0);
  const manualsLabel = t("facilitySelection.nav.manualsButton");

  const card = (
    <div
      className={cn(
        "bg-background relative flex min-h-[66px] min-w-0 flex-1 flex-col gap-3 rounded-xl border p-3 transition-shadow sm:flex-row sm:items-center sm:gap-3.5 sm:pl-[18px]",
        selected
          ? "border-orange ring-orange shadow-md ring-1 ring-inset"
          : "border-card shadow-[0_1px_2px_rgb(18_36_52/0.05),0_2px_8px_rgb(18_36_52/0.04)] hover:shadow-md",
      )}
    >
      {onSelect ? (
        <AriaButton
          aria-label={t("facilitySelection.nav.select", { name: node.name })}
          aria-pressed={selected}
          onPress={() => onSelect(node.id)}
          className="focus-visible:ring-ring/50 absolute inset-0 cursor-pointer rounded-xl border-0 bg-transparent p-0 outline-none focus-visible:ring-3"
        />
      ) : null}

      <div className="pointer-events-none relative flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Facility name + the favorite star right beside it (hidden in restore-only mode — parity w/ classic). */}
        <div className="flex min-w-0 items-center gap-1">
          <h3 className="min-w-0 truncate text-[15px] leading-5 font-semibold">{node.name}</h3>
          {!archived && showFavorite ? (
            <span className="pointer-events-auto shrink-0">
              <FavoriteStarButton name={node.name} isFavorite={isFavorite} onToggle={() => onToggleFavorite(node.id)} />
            </span>
          ) : null}
        </div>
        <FacilityCountsMeta
          labelled
          subfacilities={node.childCount}
          categories={node.categoryCount ?? 0}
          manuals={node.manualCount ?? 0}
        />
      </div>

      <div className="relative flex shrink-0 items-center gap-1.5 empty:hidden max-sm:flex-wrap">
        {navigable && canOpenManuals ? (
          <NavButton
            variant="accent"
            className="h-[38px] rounded-[10px] px-3 text-[13px] font-semibold max-sm:flex-[1_1_40%]"
            aria-label={t("facilitySelection.nav.openManuals", { name: node.name })}
            href={facilityManualsHref(node.id)}
            onNavigate={() => onOpenManuals(node.id)}
          >
            <ManualIcon aria-hidden="true" />
            {manualsLabel}
          </NavButton>
        ) : navigable && hasChildren ? (
          // Keeps Unterbereiche in its column when there are no manuals (see the slot rule above).
          <span aria-hidden="true" className="invisible inline-flex h-[38px] items-center gap-1.5 px-3 text-[13px] font-semibold max-sm:hidden">
            <ManualIcon className="size-4" />
            {manualsLabel}
          </span>
        ) : null}
        {navigable && hasChildren ? (
          <NavButton
            variant="outline"
            className="h-[38px] rounded-[10px] px-3 text-[13px] font-semibold max-sm:flex-[1_1_40%]"
            aria-label={t("facilitySelection.nav.openSubfacilities", { name: node.name })}
            href={facilityDrillHref(node.id)}
            onNavigate={() => onDrill(node.id)}
          >
            {t("facilitySelection.nav.subfacilitiesButton")}
            <ChevronRight aria-hidden="true" />
          </NavButton>
        ) : null}
        {/* Restore-only: an Unarchive button on the subtree root (never the kebab — parity with classic's Archived tab). */}
        {archived && onUnarchive ? (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            aria-label={t("facilitySelection.nav.unarchiveNode", { name: node.name })}
            onPress={onUnarchive}
          >
            <ArchiveRestore aria-hidden="true" className="size-3.5" />
            {t("facilitySelection.nav.unarchive")}
          </Button>
        ) : null}
        {/* Kebab LAST → its `right top` popover opens into the gutter, outside the list. Suppressed in restore-only mode. */}
        {!archived && kebab ? <FacilityKebabMenu {...kebab} showInsertAbove={false} tabbable /> : null}
      </div>
    </div>
  );

  // Domino stagger — capped so a large level/list doesn't wait seconds. Inline = dynamic (per-index), like the tree's pad.
  const delay = { animationDelay: `${Math.min(index, 12) * 45}ms` };

  if (flat) {
    // FLAT variant — the ancestor path (if any) sits above the card; no timeline. Root facilities carry no path.
    return (
      <li className="flex flex-col" style={delay}>
        {pathLabel ? <p className="text-muted-foreground truncate px-1 pt-1 text-xs">{pathLabel}</p> : null}
        {card}
      </li>
    );
  }

  return (
    <li className="relative flex min-w-0" style={delay}>
      {timeline ? (
        // This node's dot on the rail the LIST draws (FacilityRailList) — orange when selected.
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-1/2 -left-[26px] z-10 size-3.5 -translate-y-1/2 rounded-full border-2",
            selected ? "border-orange bg-orange" : "border-border bg-background",
          )}
        />
      ) : null}
      {card}
    </li>
  );
}
